import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronsDownUp, ChevronsUpDown, Upload } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import LayersTreeNode from './LayersTreeNode';
import LayersContextMenu from './LayersContextMenu';
import { getComponentAtPath as treeGetComponentAtPath } from '../utils/treeUtils';

/**
 * Drop zone for section reordering
 */
function SectionDropZone({ index, isDragActive, isSectionDrag }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `section-drop::${index}`,
    disabled: !isDragActive || !isSectionDrag,
    data: {
      type: 'section-drop',
      index,
    }
  });

  // Only show when dragging a section
  if (!isDragActive || !isSectionDrag) return null;

  return (
    <div
      ref={setNodeRef}
      className={`section-drop-zone mx-2 my-0.5 rounded transition-all duration-150 ${
        isOver
          ? 'bg-blue-400 h-1.5'
          : 'bg-gray-200 h-0.5'
      }`}
    />
  );
}

/**
 * LayersTree - Recursive tree view of all sections and components
 *
 * Selection sync:
 * - Reads selectedComponentPath from section.content.selectedComponentPath
 * - Updates via handleQuickUpdate(sectionId, { selectedComponentPath: path })
 */
export default function LayersTree({
  sections,
  selectedSection,
  setSelectedSection,
  allSectionTypes,
  handleDeleteSection,
  handleQuickUpdate,
  onShowComponentPalette,
  onMoveComponent,
  onExportSection,
  onImportSection,
  onReorderSections,
  // Site sections props
  siteSections,
  page,
  onSaveAsSiteSection,
}) {
  // Track which nodes are expanded
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Drag and drop state
  const [activeId, setActiveId] = useState(null);
  const [activeComponent, setActiveComponent] = useState(null);
  const [isSectionDrag, setIsSectionDrag] = useState(false);

  // Configure sensors with activation constraint to distinguish from clicks
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Local hover state for tree (syncs to preview via callback if provided)
  const [hoveredPath, setHoveredPath] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);

  // Rename state (sections and components)
  const [renamingSectionId, setRenamingSectionId] = useState(null);
  const [renamingComponentPath, setRenamingComponentPath] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const handleStartRename = useCallback((section) => {
    setRenamingSectionId(section.id);
    setRenamingComponentPath(null);
    const sectionInfo = allSectionTypes[section.section_type] || {};
    setRenameValue(section.content?.name || sectionInfo.label || section.section_type);
    setContextMenu(null); // Close context menu if open
  }, [allSectionTypes]);

  const handleStartComponentRename = useCallback((sectionId, componentPath, component) => {
    setRenamingSectionId(sectionId);
    setRenamingComponentPath(componentPath);
    setRenameValue(component.props?.name || getComponentLabel(component));
    setContextMenu(null);
  }, []);

  const handleRenameSubmit = useCallback(() => {
    if (renamingSectionId && renameValue.trim()) {
      if (renamingComponentPath) {
        // Component rename — select it, then update name prop
        handleQuickUpdate(renamingSectionId, { selectedComponentPath: renamingComponentPath });
        handleQuickUpdate(renamingSectionId, { actualColumns: [{ name: renameValue.trim() }] });
      } else {
        // Section rename
        handleQuickUpdate(renamingSectionId, { name: renameValue.trim() });
      }
    }
    setRenamingSectionId(null);
    setRenamingComponentPath(null);
    setRenameValue('');
  }, [renamingSectionId, renamingComponentPath, renameValue, handleQuickUpdate]);

  const handleRenameCancel = useCallback(() => {
    setRenamingSectionId(null);
    setRenamingComponentPath(null);
    setRenameValue('');
  }, []);

  // Keyboard navigation state
  const treeContainerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const toggleExpanded = useCallback((nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Collect all expandable node IDs for expand/collapse all
  const allExpandableIds = useMemo(() => {
    const ids = new Set();

    const collectIds = (components, sectionId, basePath = '') => {
      components.forEach((component, index) => {
        const componentPath = basePath ? `${basePath}.children.${index}` : `${index}`;
        const nodeId = `${sectionId}.${componentPath}`;
        if (component.children?.length > 0) {
          ids.add(nodeId);
          collectIds(component.children, sectionId, componentPath);
        }
      });
    };

    sections.forEach((section) => {
      if (section.section_type === 'layout-1') {
        const components = section.content?.components || [];
        if (components.length > 0) {
          ids.add(section.id); // Section itself is expandable
          collectIds(components, section.id);
        }
      }
    });

    return ids;
  }, [sections]);

  const expandAll = () => setExpandedNodes(new Set(allExpandableIds));
  const collapseAll = () => setExpandedNodes(new Set());

  /**
   * Build a flat list of all visible items in the tree for keyboard navigation.
   * Respects expansion state - only includes children of expanded nodes.
   */
  const getVisibleItems = useCallback(() => {
    const items = [];

    const traverseComponents = (components, sectionId, section, basePath = '') => {
      components.forEach((component, index) => {
        const componentPath = basePath ? `${basePath}.children.${index}` : `${index}`;
        const nodeId = `${sectionId}.${componentPath}`;
        const hasChildren = component.children?.length > 0;

        items.push({
          type: 'component',
          sectionId,
          section,
          componentPath,
          nodeId,
          component,
          hasChildren,
        });

        // Only traverse children if this node is expanded
        if (hasChildren && expandedNodes.has(nodeId)) {
          traverseComponents(component.children, sectionId, section, componentPath);
        }
      });
    };

    sections.forEach((section) => {
      const isLayoutSection = section.section_type === 'layout-1';
      const components = section.content?.components || [];
      const hasChildren = isLayoutSection && components.length > 0;

      // Add the section itself
      items.push({
        type: 'section',
        sectionId: section.id,
        section,
        componentPath: null,
        nodeId: section.id,
        hasChildren,
      });

      // If section is expanded, traverse its components
      if (isLayoutSection && expandedNodes.has(section.id) && components.length > 0) {
        traverseComponents(components, section.id, section);
      }
    });

    return items;
  }, [sections, expandedNodes]);

  /**
   * Get the parent node ID for a given visible item
   */
  const getParentNodeId = useCallback((item) => {
    if (item.type === 'section') {
      return null; // Sections have no parent in the tree
    }

    // For components, find the parent
    const { componentPath, sectionId } = item;
    if (!componentPath.includes('.')) {
      // Root-level component, parent is the section
      return sectionId;
    }

    // Path like "0.children.1.children.2" -> parent is "0.children.1"
    const lastChildrenIndex = componentPath.lastIndexOf('.children.');
    if (lastChildrenIndex === -1) {
      return sectionId;
    }

    const parentPath = componentPath.substring(0, lastChildrenIndex);
    return `${sectionId}.${parentPath}`;
  }, []);

  /**
   * Select an item from the visible items list
   */
  const selectVisibleItem = useCallback((item) => {
    if (item.type === 'section') {
      setSelectedSection(item.section);
      if (handleQuickUpdate && item.section.section_type === 'layout-1') {
        handleQuickUpdate(item.sectionId, { selectedComponentPath: null });
      }
    } else {
      setSelectedSection(item.section);
      if (handleQuickUpdate) {
        handleQuickUpdate(item.sectionId, { selectedComponentPath: item.componentPath });
      }
    }
  }, [setSelectedSection, handleQuickUpdate]);

  /**
   * Get the current selection as a nodeId for comparison with visible items
   */
  const getCurrentSelectionNodeId = useCallback(() => {
    if (!selectedSection) return null;

    const selectedPath = selectedSection.content?.selectedComponentPath;
    if (selectedPath) {
      return `${selectedSection.id}.${selectedPath}`;
    }
    return selectedSection.id;
  }, [selectedSection]);

  /**
   * Handle keyboard navigation in the layers tree
   */
  const handleKeyDown = useCallback((e) => {
    // Only handle if tree is focused
    if (!isFocused) return;

    // Don't handle navigation while renaming
    if (renamingSectionId || renamingComponentPath) return;

    // Don't interfere with text inputs
    const target = e.target;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // Only handle arrow keys
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      return;
    }

    e.preventDefault();

    const visibleItems = getVisibleItems();
    if (visibleItems.length === 0) return;

    const currentNodeId = getCurrentSelectionNodeId();
    const currentIndex = visibleItems.findIndex(item => item.nodeId === currentNodeId);

    switch (e.key) {
      case 'ArrowDown': {
        if (currentIndex === -1) {
          // No selection, select first item
          selectVisibleItem(visibleItems[0]);
        } else if (currentIndex < visibleItems.length - 1) {
          // Move to next visible item
          selectVisibleItem(visibleItems[currentIndex + 1]);
        }
        break;
      }

      case 'ArrowUp': {
        if (currentIndex === -1) {
          // No selection, select last item
          selectVisibleItem(visibleItems[visibleItems.length - 1]);
        } else if (currentIndex > 0) {
          // Move to previous visible item
          selectVisibleItem(visibleItems[currentIndex - 1]);
        }
        break;
      }

      case 'ArrowRight': {
        if (currentIndex === -1) return;

        const currentItem = visibleItems[currentIndex];
        if (!currentItem.hasChildren) return;

        const isExpanded = expandedNodes.has(currentItem.nodeId);

        if (!isExpanded) {
          // Expand the node
          toggleExpanded(currentItem.nodeId);
        } else {
          // Already expanded, move to first child (next item in visible list)
          if (currentIndex < visibleItems.length - 1) {
            const nextItem = visibleItems[currentIndex + 1];
            // Verify next item is actually a child (its nodeId starts with current nodeId)
            if (nextItem.nodeId.startsWith(currentItem.nodeId + '.')) {
              selectVisibleItem(nextItem);
            }
          }
        }
        break;
      }

      case 'ArrowLeft': {
        if (currentIndex === -1) return;

        const currentItem = visibleItems[currentIndex];
        const isExpanded = currentItem.hasChildren && expandedNodes.has(currentItem.nodeId);

        if (isExpanded) {
          // Collapse the node
          toggleExpanded(currentItem.nodeId);
        } else {
          // Move to parent
          const parentNodeId = getParentNodeId(currentItem);
          if (parentNodeId) {
            const parentItem = visibleItems.find(item => item.nodeId === parentNodeId);
            if (parentItem) {
              selectVisibleItem(parentItem);
            }
          }
        }
        break;
      }

      default:
        break;
    }
  }, [isFocused, renamingSectionId, renamingComponentPath, getVisibleItems, getCurrentSelectionNodeId, selectVisibleItem, expandedNodes, toggleExpanded, getParentNodeId]);

  // Attach keyboard listener to document when focused
  useEffect(() => {
    if (isFocused) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFocused, handleKeyDown]);

  // Auto-expand tree when an element is selected in the preview panel
  useEffect(() => {
    const selectedPath = selectedSection?.content?.selectedComponentPath;
    const sectionId = selectedSection?.id;

    // Do nothing if no selection or no section
    if (!selectedPath || !sectionId) return;

    // Compute all parent paths that need to be expanded
    // For path "0.children.1.children.2", we need to expand:
    //   - the section itself (sectionId)
    //   - "0" (sectionId.0)
    //   - "0.children.1" (sectionId.0.children.1)
    // The selected element itself doesn't need to be expanded
    const pathsToExpand = new Set();

    // Always expand the section itself
    pathsToExpand.add(sectionId);

    // Split the path and build parent paths
    const parts = selectedPath.split('.');
    let currentPath = '';

    for (let i = 0; i < parts.length; i++) {
      if (i === 0) {
        // First part is the root index (e.g., "0")
        currentPath = parts[i];
      } else {
        // Subsequent parts include "children" and index
        currentPath += '.' + parts[i];
      }

      // Only add parent paths (not the final selected element)
      // The final element is at index parts.length - 1 (if it ends with an index after "children")
      // Since paths are like "0.children.1.children.2", the element itself is the full path
      // We want all paths except the full selected path
      if (currentPath !== selectedPath) {
        pathsToExpand.add(`${sectionId}.${currentPath}`);
      }
    }

    // Also expand the selected element itself if it has children
    // This ensures newly added components with children (like rows with columns) are expanded
    const components = selectedSection?.content?.components || [];
    const selectedComponent = treeGetComponentAtPath(components, selectedPath);
    if (selectedComponent?.children?.length > 0) {
      pathsToExpand.add(`${sectionId}.${selectedPath}`);
    }

    // Add all computed paths to expandedNodes (preserve existing expanded state)
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      pathsToExpand.forEach((path) => next.add(path));
      return next;
    });
  }, [selectedSection?.content?.selectedComponentPath, selectedSection?.id, selectedSection?.content?.components]);

  // Scroll to selected element in layers panel after expansion
  useEffect(() => {
    const selectedPath = selectedSection?.content?.selectedComponentPath;
    const sectionId = selectedSection?.id;

    if (!sectionId) return;

    // Build the layer ID to find
    const layerId = selectedPath
      ? `${sectionId}.${selectedPath}`
      : `section-${sectionId}`;

    // Small delay to allow DOM to update after expansion
    const timeoutId = setTimeout(() => {
      const layerElement = document.querySelector(`[data-layer-id="${layerId}"]`);
      if (layerElement) {
        layerElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedSection?.content?.selectedComponentPath, selectedSection?.id]);

  const handleContextMenu = (e, nodeData) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      ...nodeData,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // Helper to find a component at a given path within a section's components
  const findComponentAtPath = (components, path) => {
    if (!path || !components) return null;
    const parts = path.split('.');
    let current = components[parseInt(parts[0])];
    for (let i = 1; i < parts.length; i += 2) {
      if (parts[i] === 'children' && current?.children) {
        current = current.children[parseInt(parts[i + 1])];
      }
    }
    return current;
  };

  // Parse drop zone ID to extract target information
  // Drop zone IDs follow the format: "drop::{sectionId}::{parentPath}::{index}" or "drop::{sectionId}::root::{index}"
  // Using :: as delimiter since section IDs are UUIDs (contain dashes)
  const parseDropZoneId = (dropId) => {
    if (!dropId || !dropId.startsWith('drop::')) return null;

    const parts = dropId.substring(6).split('::');  // Remove 'drop::' prefix
    if (parts.length !== 3) return null;

    const [sectionId, parentPathOrRoot, indexStr] = parts;
    const targetIndex = parseInt(indexStr);

    if (isNaN(targetIndex)) return null;

    // Check for root level
    const targetParentPath = parentPathOrRoot === 'root' ? null : parentPathOrRoot;

    return { sectionId, targetParentPath, targetIndex };
  };

  // Drag event handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    // Check if this is a section drag
    if (active.data.current?.type === 'section') {
      setIsSectionDrag(true);
      setActiveComponent(null);
      return;
    }

    setIsSectionDrag(false);

    // Existing component logic...
    const [sectionId, ...pathParts] = active.id.split('.');
    const componentPath = pathParts.join('.');
    const section = sections.find(s => s.id === sectionId);

    if (section && section.content?.components) {
      const component = findComponentAtPath(section.content.components, componentPath);
      setActiveComponent(component);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveComponent(null);
    setIsSectionDrag(false);

    if (!over || active.id === over.id) return;

    // Handle section drag
    if (active.data.current?.type === 'section') {
      // Parse section ID from active.id (format: "section::{sectionId}")
      const sectionId = active.id.replace('section::', '');

      // Parse target index from over.id (format: "section-drop::{index}")
      if (!over.id.startsWith('section-drop::')) return;
      const targetIndex = parseInt(over.id.replace('section-drop::', ''));

      if (isNaN(targetIndex)) return;

      // Call the reorder handler
      if (onReorderSections) {
        onReorderSections(sectionId, targetIndex);
      }
      return;
    }

    // Existing component move logic...
    // Parse source: active.id format is "{sectionId}.{componentPath}"
    const [sourceSectionId, ...sourcePathParts] = active.id.split('.');
    const sourcePath = sourcePathParts.join('.');

    // Parse target from drop zone id
    const dropInfo = parseDropZoneId(over.id);
    if (!dropInfo) return;

    const { sectionId: targetSectionId, targetParentPath, targetIndex } = dropInfo;

    // Call the move handler if provided
    if (onMoveComponent) {
      onMoveComponent(sourceSectionId, sourcePath, targetParentPath, targetIndex, targetSectionId);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        ref={treeContainerRef}
        className={`py-1 outline-none ${isFocused ? 'ring-1 ring-inset ring-blue-200' : ''}`}
        tabIndex={0}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={closeContextMenu}
      >
        {/* Toolbar - Import, Expand/Collapse All */}
        <div className="flex items-center justify-between gap-1 px-2 pb-1 mb-1 border-b border-gray-100">
          <button
            onClick={onImportSection}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Import section"
          >
            <Upload className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-center gap-1">
            {allExpandableIds.size > 0 && (
              <>
                <button
                  onClick={expandAll}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Expand all"
                >
                  <ChevronsUpDown className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={collapseAll}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Collapse all"
                >
                  <ChevronsDownUp className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        {sections.map((section, sectionIndex) => {
          const sectionInfo = allSectionTypes[section.section_type] || {};
          const isLayoutSection = section.section_type === 'layout-1';
          const components = section.content?.components || [];
          const isExpanded = expandedNodes.has(section.id);
          const isSectionSelected = selectedSection?.id === section.id;

          // For layout sections, read selectedComponentPath from section.content
          const selectedComponentPath = isSectionSelected
            ? section.content?.selectedComponentPath || null
            : null;

          return (
            <React.Fragment key={section.id}>
              {/* Section Drop Zone (before this section) */}
              <SectionDropZone
                index={sectionIndex}
                isDragActive={!!activeId}
                isSectionDrag={isSectionDrag}
              />

              {/* Section Node */}
              <LayersTreeNode
                type="section"
                section={section}
                sectionType={section.section_type}
                label={section.content?.name || sectionInfo.label || section.section_type}
                icon={sectionInfo.icon}
                isSelected={isSectionSelected && !selectedComponentPath}
                isExpanded={isExpanded}
                hasChildren={isLayoutSection && components.length > 0}
                depth={0}
                onToggleExpand={() => toggleExpanded(section.id)}
                onClick={() => {
                  setSelectedSection(section);
                  // Clear component selection when clicking section
                  if (handleQuickUpdate && isLayoutSection) {
                    handleQuickUpdate(section.id, { selectedComponentPath: null });
                  }
                }}
                onContextMenu={(e) => handleContextMenu(e, {
                  type: 'section',
                  section,
                  sectionIndex,
                })}
                sectionIndex={sectionIndex}
                siteSections={siteSections}
                isRenaming={renamingSectionId === section.id && !renamingComponentPath}
                renameValue={renameValue}
                onRenameChange={setRenameValue}
                onRenameSubmit={handleRenameSubmit}
                onRenameCancel={handleRenameCancel}
                onStartRename={() => handleStartRename(section)}
              />

              {/* Nested Components (for layout sections) */}
              {isLayoutSection && isExpanded && components.length > 0 && (
                <ComponentNodes
                  components={components}
                  basePath=""
                  parentPath={null}
                  section={section}
                  sectionId={section.id}
                  selectedComponentPath={selectedComponentPath}
                  hoveredPath={isSectionSelected ? hoveredPath : null}
                  setHoveredPath={setHoveredPath}
                  handleQuickUpdate={handleQuickUpdate}
                  setSelectedSection={setSelectedSection}
                  expandedNodes={expandedNodes}
                  toggleExpanded={toggleExpanded}
                  onContextMenu={handleContextMenu}
                  depth={1}
                  activeId={activeId}
                  isDragActive={!!activeId}
                  renamingComponentPath={renamingComponentPath}
                  renamingSectionId={renamingSectionId}
                  renameValue={renameValue}
                  onRenameChange={setRenameValue}
                  onRenameSubmit={handleRenameSubmit}
                  onRenameCancel={handleRenameCancel}
                  onStartComponentRename={handleStartComponentRename}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Final drop zone (after all sections) */}
        <SectionDropZone
          index={sections.length}
          isDragActive={!!activeId}
          isSectionDrag={isSectionDrag}
        />

        {/* Context Menu */}
        {contextMenu && (
          <LayersContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            type={contextMenu.type}
            section={contextMenu.section}
            sectionIndex={contextMenu.sectionIndex}
            component={contextMenu.component}
            componentPath={contextMenu.componentPath}
            onClose={closeContextMenu}
            handleDeleteSection={handleDeleteSection}
            handleQuickUpdate={handleQuickUpdate}
            onShowComponentPalette={onShowComponentPalette}
            onExportSection={onExportSection}
            onImportSection={onImportSection}
            page={page}
            onSaveAsSiteSection={onSaveAsSiteSection}
            onRenameSection={handleStartRename}
            onRenameComponent={handleStartComponentRename}
          />
        )}
      </div>

      {/* Drag Overlay - shows dragged component preview */}
      <DragOverlay>
        {activeId ? (
          <div className="layers-drag-overlay px-2 py-1 bg-white border border-blue-300 rounded shadow-lg text-xs font-medium text-gray-700">
            {isSectionDrag
              ? (() => {
                  const sectionId = activeId.replace('section::', '');
                  const section = sections.find(s => s.id === sectionId);
                  const sectionInfo = allSectionTypes[section?.section_type] || {};
                  return section?.content?.name || sectionInfo.label || section?.section_type || 'Section';
                })()
              : activeComponent ? getComponentLabel(activeComponent) : 'Moving...'
            }
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * Recursive component to render nested components
 */
function ComponentNodes({
  components,
  basePath,
  parentPath,
  section,
  sectionId,
  selectedComponentPath,
  hoveredPath,
  setHoveredPath,
  handleQuickUpdate,
  setSelectedSection,
  expandedNodes,
  toggleExpanded,
  onContextMenu,
  depth,
  activeId,
  isDragActive,
  renamingComponentPath,
  renamingSectionId,
  renameValue,
  onRenameChange,
  onRenameSubmit,
  onRenameCancel,
  onStartComponentRename,
}) {
  return components.map((component, index) => {
    const componentPath = basePath ? `${basePath}.children.${index}` : `${index}`;
    const nodeId = `${sectionId}.${componentPath}`;
    const isSelected = selectedComponentPath === componentPath;
    const isHovered = hoveredPath === componentPath && !isSelected;
    const hasChildren = component.children?.length > 0;
    const isExpanded = expandedNodes.has(nodeId);
    const isDragging = activeId === `${sectionId}.${componentPath}`;

    return (
      <React.Fragment key={component.id || componentPath}>
        <LayersTreeNode
          type="component"
          component={component}
          componentType={component.type}
          label={getComponentLabel(component)}
          isSelected={isSelected}
          isHovered={isHovered}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          depth={depth}
          onToggleExpand={() => toggleExpanded(nodeId)}
          onClick={() => {
            // First select the section
            setSelectedSection(section);
            // Then update the selectedComponentPath in section content
            if (handleQuickUpdate) {
              handleQuickUpdate(sectionId, { selectedComponentPath: componentPath });
            }
          }}
          onMouseEnter={() => setHoveredPath(componentPath)}
          onMouseLeave={() => {
            if (hoveredPath === componentPath) {
              setHoveredPath(null);
            }
          }}
          onContextMenu={(e) => onContextMenu(e, {
            type: 'component',
            section,
            component,
            componentPath,
          })}
          // New props for drag and drop
          sectionId={sectionId}
          componentPath={componentPath}
          parentPath={parentPath}
          index={index}
          isDragging={isDragging}
          isDragActive={isDragActive}
          isRenaming={renamingSectionId === sectionId && renamingComponentPath === componentPath}
          renameValue={renameValue}
          onRenameChange={onRenameChange}
          onRenameSubmit={onRenameSubmit}
          onRenameCancel={onRenameCancel}
          onStartRename={() => onStartComponentRename(sectionId, componentPath, component)}
        />

        {/* Nested children */}
        {hasChildren && isExpanded && (
          <ComponentNodes
            components={component.children}
            basePath={componentPath}
            parentPath={componentPath}
            section={section}
            sectionId={sectionId}
            selectedComponentPath={selectedComponentPath}
            hoveredPath={hoveredPath}
            setHoveredPath={setHoveredPath}
            handleQuickUpdate={handleQuickUpdate}
            setSelectedSection={setSelectedSection}
            expandedNodes={expandedNodes}
            toggleExpanded={toggleExpanded}
            onContextMenu={onContextMenu}
            depth={depth + 1}
            activeId={activeId}
            isDragActive={isDragActive}
            renamingComponentPath={renamingComponentPath}
            renamingSectionId={renamingSectionId}
            renameValue={renameValue}
            onRenameChange={onRenameChange}
            onRenameSubmit={onRenameSubmit}
            onRenameCancel={onRenameCancel}
            onStartComponentRename={onStartComponentRename}
          />
        )}
      </React.Fragment>
    );
  });
}

/**
 * Get a display label for a component
 */
function getComponentLabel(component) {
  // Return custom name if set
  if (component.props?.name) {
    return component.props.name;
  }

  const labels = {
    row: 'Row',
    column: 'Column',
    text: 'Text',
    image: 'Image',
    button: 'Button',
    icon: 'Icon',
    spacer: 'Spacer',
    video: 'Video',
    listItem: 'List Item',
    container: 'Container',
    accordion: 'Accordion',
    link: 'Link',
  };

  let label = labels[component.type] || component.type;

  // Add some context for text components
  if (component.type === 'text' && component.props?.content) {
    const preview = component.props.content.substring(0, 20);
    if (preview) {
      label = `Text: "${preview}${component.props.content.length > 20 ? '...' : ''}"`;
    }
  }

  return label;
}
